// Inspector section for a selected element's lifecycle.
//
// Discoverability: without this, decorations are invisible unless you already
// know to type `@`. Selecting a planned or deprecated node should say so in
// words, name the window, and — for a deprecation — surface the `use:` pointer
// and `reason:` the author left, which is the entire point of writing them.

import type { DecorationRef } from "@archlang/scene/arch-model";
import { useTranslation } from "../../i18n";

export interface LifecycleSectionProps {
  /** Decorations written on the selected element. */
  readonly decorations: readonly DecorationRef[] | undefined;
  /** Resolved state at the current scrub point, when the element has a window. */
  readonly state?: string;
}

function argOf(inst: DecorationRef, name: string): string | undefined {
  const named = inst.args.find((a) => a.name === name);
  if (named) return named.value;
  // The single bare positional binds the primary field — `from` for BOTH
  // lifecycle decorations, so a lone marker always names where the transition
  // starts (`@planned(v3)` arrives at v3, `@deprecated(v3)` sunsets from v3).
  if (name === "from") return inst.args.find((a) => a.name === undefined)?.value;
  return undefined;
}

export function LifecycleSection({ decorations, state }: LifecycleSectionProps) {
  const { t } = useTranslation("lifecycle");
  const lifecycle = (decorations ?? []).filter(
    (d) => d.name === "planned" || d.name === "deprecated",
  );
  if (lifecycle.length === 0) return null;

  return (
    <div data-testid="inspector-lifecycle">
      <div className="text-xs uppercase text-slate-500 mb-1">{t("heading")}</div>
      {state && (
        <div className="mb-1 text-sm text-slate-200">
          {t(`state.${state}`, { defaultValue: state })}
          <span className="ml-1 text-xs text-slate-500">{t("atPoint")}</span>
        </div>
      )}
      <div className="space-y-1">
        {lifecycle.map((d) => {
          const from = argOf(d, "from");
          const to = argOf(d, "to");
          const use = d.name === "deprecated" ? argOf(d, "use") : undefined;
          const reason = d.name === "deprecated" ? argOf(d, "reason") : undefined;
          const window =
            from && to
              ? t("window.range", { from, to })
              : to
                ? t("window.by", { to })
                : from
                  ? t("window.from", { from })
                  : t("window.none");
          return (
            <div key={d.name} className="text-xs text-slate-400">
              <span className="text-slate-300">@{d.name}</span> <span>{window}</span>
              {use && (
                <div>
                  {t("useInstead")} <span className="text-slate-300">{use}</span>
                </div>
              )}
              {reason && <div className="italic">{reason}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
